import { ArrowIcon } from "../../Components/Brand/Icons";
import LineField from "../../Components/Form/LineField";
import { CONTACT, SOCIAL } from "../../Components/Layout/nav";
import StationTrack from "../../Components/Track/StationTrack";
import { NEXT_STEPS, SERVICE_CHOICES } from "../../content/contact";
import { useContactForm } from "../../hooks/useContactForm";
import Seo from "../../seo/Seo";
import { breadcrumbSchema } from "../../seo/site";
import "./contact.css";

const ContactPage = () => {
  const { fields, setField, services, toggleService, errors, status, submit } = useContactForm();

  return (
    <>
      <Seo
        path="/contact"
        title="Contact Us — Start Your Project | Infinitude Stack"
        description="Tell us what you're building. Call +1 (519) 504-9076, email info@infinitudestack.com or book a free 30-minute call with our Kitchener team."
        schema={[breadcrumbSchema("Contact", "/contact")]}
      />

      <div className="is-contact">
        {/* ---------- Hero ---------- */}
        <section className="is-container is-contact-hero">
          <p className="is-eyebrow">Contact</p>
          <h1 className="is-display is-contact-hero__title">
            Let’s talk about <strong className="is-accent-text">what you’re building.</strong>
          </h1>
          <p className="is-lede is-contact-hero__lede">
            A new product, a project that’s stuck, or just a second opinion — tell us
            where you are and we’ll tell you how we can help.
          </p>
          <a
            href={CONTACT.calendly}
            target="_blank"
            rel="noopener noreferrer"
            className="is-underline-link is-contact-hero__call"
          >
            Prefer to talk? Book a 30-minute call →
          </a>
        </section>

        {/* ---------- Form + direct lines ---------- */}
        <section className="is-contact-main" aria-labelledby="contact-form-title">
          <div className="is-contact__panel">
            <form className="is-contact__form" onSubmit={submit} noValidate>
              <h2 id="contact-form-title" className="is-contact__sr">
                Send us a message
              </h2>

              <fieldset className="is-contact__services">
                <legend className="is-contact__label">What can we help with?</legend>
                <div className="is-picker is-picker--dark">
                  {SERVICE_CHOICES.map((s) => {
                    const on = services.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        className={"is-picker__item" + (on ? " is-active" : "")}
                        aria-pressed={on}
                        onClick={() => toggleService(s)}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div className="is-field-row">
                <LineField
                  id="contact-name"
                  label="Name"
                  value={fields.name}
                  onChange={setField("name")}
                  error={errors.name}
                  placeholder="Your full name"
                  autoComplete="name"
                  required
                />
                <LineField
                  id="contact-email"
                  label="Email"
                  type="email"
                  value={fields.email}
                  onChange={setField("email")}
                  error={errors.email}
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                />
              </div>
              <div className="is-field-row">
                <LineField
                  id="contact-phone"
                  label="Phone"
                  type="tel"
                  value={fields.phone}
                  onChange={setField("phone")}
                  placeholder="Optional"
                  autoComplete="tel"
                />
                <LineField
                  id="contact-company"
                  label="Company"
                  value={fields.company}
                  onChange={setField("company")}
                  placeholder="Optional"
                  autoComplete="organization"
                />
              </div>
              <LineField
                id="contact-project"
                label="Your project"
                multiline
                rows={4}
                value={fields.message}
                onChange={setField("message")}
                error={errors.message}
                placeholder="Goals, timeline, anything we should know"
                required
              />

              <div className="is-contact__submit">
                <p className="is-contact__status" role="status">
                  {status === "success" && "Thanks — your message is on its way. We’ll be in touch soon."}
                  {status === "error" && "Something went wrong. Please try again, or email us directly."}
                  {status !== "success" &&
                    status !== "error" &&
                    (services.length
                      ? `Interested in: ${services.join(", ")}`
                      : "Pick any that fit — or none if you’re not sure yet.")}
                </p>
                <button type="submit" className="is-btn is-btn--accent" disabled={status === "sending"}>
                  {status === "sending" ? "Sending…" : "Send message"}
                  <span className="is-btn__arrow">
                    <ArrowIcon />
                  </span>
                </button>
              </div>
            </form>

            <aside className="is-contact__direct" aria-label="Reach us directly">
              <p className="is-contact__label">Or reach us directly</p>
              <ul className="is-contact__lines">
                <li>
                  <span className="is-contact__line-label">Call</span>
                  <a href={CONTACT.phoneHref}>{CONTACT.phoneDisplay}</a>
                </li>
                <li>
                  <span className="is-contact__line-label">Email</span>
                  <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
                </li>
                <li>
                  <span className="is-contact__line-label">Book a call</span>
                  <a href={CONTACT.calendly} target="_blank" rel="noopener noreferrer">
                    30 minutes on Calendly ↗
                  </a>
                </li>
                <li className="is-contact__line--end">
                  <span className="is-contact__line-label">Studio</span>
                  <span>{CONTACT.location}</span>
                </li>
              </ul>
              <p className="is-contact__social">
                {SOCIAL.map((s) => (
                  <a key={s.href} href={s.href} target="_blank" rel="noopener noreferrer">
                    {s.label} ↗
                  </a>
                ))}
              </p>
            </aside>
          </div>
        </section>

        {/* ---------- What happens next ---------- */}
        <section className="is-container is-contact-next" aria-labelledby="contact-next-title">
          <div className="is-section-head is-reveal">
            <div>
              <p className="is-eyebrow">What happens next</p>
              <h2 id="contact-next-title" className="is-display is-h2">
                Three steps <strong>to getting started.</strong>
              </h2>
            </div>
          </div>
          <StationTrack items={NEXT_STEPS} />
        </section>
      </div>
    </>
  );
};

export default ContactPage;
